import type { ReactNode } from "react";
import type { MetricActionProps } from "@studio";
import { ChartPanel, HeatmapChart, KpiCard as McKpiCard, SeriesChart, ShareDonut, Sparkline as McSparkline } from "./components";
import { tone } from "./theme";

/**
 * 시안 디자인 시스템 차트 모듈(vendor/…/design-system/patterns/charts.tsx) 자리를 대신한다 (vite.config.ts 의 meta-crew-charts 플러그인이
 * 그 파일을 가져오는 모든 import 를 이 파일로 돌림). 시안 부품(사용량 탐색기·경쟁 분석 등)이 쓰는 이름·props 를 그대로 받아 콘솔 ECharts 부품으로 그린다.
 * 시안의 SVG 차트 코드는 콘솔 번들에 들어가지 않는다. vendor 는 손대지 않는다 (sync-design 으로만 바뀜).
 * 콘솔 화면은 이 파일이 아니라 src/charts 의 부품을 직접 쓴다.
 */

export type ChartSeries = { name: string; values: (number | null)[]; previous?: (number | null)[]; color?: string; missing?: (string | null)[] };
export type ChartMarker = { index: number; label: string };
export const chartColors = [tone.main, tone.ok, tone.warn, tone.fail, tone.muted, "var(--blue-line)"];

/** 빈 값에서 끊은 구간 (시안 호환) */
export function chartSegments(values: (number | null)[]) {
  const result: number[][] = [];
  values.forEach((v, i) => {
    if (v == null) return;
    if (i === 0 || values[i - 1] == null) result.push([]);
    result[result.length - 1]!.push(i);
  });
  return result;
}

export function ChartFrame({ title, description, children, table, controls, initialView }: { title: string; description?: ReactNode; children: ReactNode; table: ReactNode; controls?: ReactNode; initialView?: "chart" | "table" }) {
  return (
    <ChartPanel title={title} description={description} table={table} controls={controls} initialView={initialView}>
      {children}
    </ChartPanel>
  );
}

export function DataChart(p: {
  title: string;
  labels: string[];
  series: ChartSeries[];
  kind?: "line" | "area" | "bar";
  stacked?: boolean;
  normalized?: boolean;
  horizontal?: boolean;
  rank?: boolean;
  logScale?: boolean;
  maxValue?: number;
  markers?: ChartMarker[];
  description?: ReactNode;
  initialView?: "chart" | "table";
  format?: (n: number | null) => string;
}) {
  return (
    <SeriesChart
      title={p.title}
      description={p.description}
      labels={p.labels}
      series={p.series}
      kind={p.kind}
      stacked={p.stacked}
      normalized={p.normalized}
      horizontal={p.horizontal}
      rank={p.rank}
      log={p.logScale}
      max={p.rank ? undefined : p.maxValue}
      markers={p.markers}
      initialView={p.initialView}
      format={p.format}
    />
  );
}

export function Sparkline({ values, rank, label }: { values: (number | null)[]; rank?: boolean; label?: string }) {
  return <McSparkline values={values} rank={rank} label={label} />;
}

export type KpiCardProps = MetricActionProps & { label: string; value: ReactNode; delta?: string; values: (number | null)[]; active?: boolean; onClick?: () => void };
export function KpiCard(p: KpiCardProps) {
  return <McKpiCard {...p} />;
}

export function Heatmap({ title, columns, rows, format, description }: { title: string; columns: string[]; rows: { name: string; values: (number | null)[] }[]; format?: (n: number | null) => string; description?: string }) {
  // 시안 히트맵은 순위 구간(1–3·4–10·11–30·31–100·밖) 전용
  return <HeatmapChart title={title} description={description} columns={columns} rows={rows} rank format={format} />;
}

export function DonutChart({ title, value, total }: { title: string; value: number; total: number }) {
  return <ShareDonut title={title} value={value} total={total} />;
}
